// Hub's rentals catalog screens (plan §9.1, Phase 6a): what bre-hub-rentals
// answers at /v1/catalog/:kind and the web Worker checks before asking it.

import type { Capability } from './capabilities'

export const RENTAL_CATALOG_BASE_KINDS = ['contract-terms', 'questionnaires', 'checklist-templates', 'layout-shapes', 'inventory', 'invoice-charge-templates', 'settlement-expense-categories'] as const
export type RentalCatalogBaseKind = (typeof RENTAL_CATALOG_BASE_KINDS)[number]
export const RENTAL_CATALOG_MORE_KINDS = ['contract-profiles', 'room-layouts', 'sponsorship-packages', 'template-bundles'] as const
export type RentalCatalogMoreKind = (typeof RENTAL_CATALOG_MORE_KINDS)[number]
export const RENTAL_CATALOG_KINDS = [...RENTAL_CATALOG_BASE_KINDS, ...RENTAL_CATALOG_MORE_KINDS] as const
export type RentalCatalogKind = (typeof RENTAL_CATALOG_KINDS)[number]

export const isRentalCatalogKind = (value: string): value is RentalCatalogKind => (RENTAL_CATALOG_KINDS as readonly string[]).includes(value)

export const isRentalCatalogMoreKind = (value: string): value is RentalCatalogMoreKind => (RENTAL_CATALOG_MORE_KINDS as readonly string[]).includes(value)

export const rentalCatalogCapability = (kind: RentalCatalogKind): Capability => (kind === 'settlement-expense-categories' ? 'events.settle' : kind === 'sponsorship-packages' || kind === 'template-bundles' ? 'sponsorships.manage' : 'rentals.manage')

export const CONTRACT_FAMILIES = ['rental', 'show', 'sponsorship'] as const
export const CONTRACT_TERM_TYPES = ['clause', 'exhibit'] as const
export const CONTRACT_ACK_REQUIREMENTS = ['none', 'checkbox', 'initials'] as const
export const CHECKLIST_ASSIGNEES = ['renter', 'bre', 'shared'] as const
export const LAYOUT_SHAPE_CATEGORIES = ['Table', 'Seating', 'Stage', 'Bar', 'Dance Floor', 'Buffet', 'Décor', 'Other'] as const
export const LAYOUT_SHAPE_TYPES = ['rect', 'circle'] as const
export const INVENTORY_CATEGORIES = ['Tables', 'Chairs', 'Linens', 'AV / Production', 'Decor', 'Bar', 'Signage', 'Other'] as const
export const QUESTION_TYPES = ['text', 'textarea', 'select', 'multiselect', 'yesno', 'number', 'date'] as const

export type RentalCatalogRow = { id: string } & Record<string, unknown>

export type RentalCatalogView = {
  kind: RentalCatalogKind
  owner: string
  canEdit: boolean
  rows: RentalCatalogRow[]
  // contract-terms: profiles using each term. layout-shapes: spaces to pick from.
  usage?: Record<string, number>
  spaces?: Array<{ id: string; name: string }>
  // sponsorship-packages: series to link. template-bundles: checklist templates.
  series?: Array<{ id: string; name: string }>
  checklists?: Array<{ id: string; label: string }>
}

export const SPONSORSHIP_INVENTORY_SCOPES = ['general', 'annual', 'series'] as const
export const SPONSORSHIP_RENEWAL_TYPES = ['none', 'standard', 'fror', 'exclusive_window'] as const
export const SPONSORSHIP_PRORATION_BASES = ['none', 'monthly', 'daily'] as const
export const TEMPLATE_BUNDLE_ENTITIES = ['rental', 'sponsorship', 'show'] as const
export const TEMPLATE_TASK_PRIORITIES = ['low', 'medium', 'high', 'urgent'] as const
export const TEMPLATE_TASK_ROLES = ['admin', 'booker', 'production', 'finance', 'read_only'] as const

// One item on a floor plan, in feet, as Booking stores it.
export type RentalLayoutElement = { id: string; shapeId: string; name: string; shapeType: 'rect' | 'circle'; x: number; y: number; widthFt: number; heightFt: number; color: string; label: string; rotation: number; seats?: number }
export type RentalLayoutShape = { id: string; name: string; category: string; shapeType: string; defaultWidthFt: number; defaultHeightFt: number; color: string; labelTemplate: string | null; seats: number | null; adminOnly: boolean }

export type RoomLayoutDetail = {
  owner: string
  canEdit: boolean
  layout: { id: string; name: string; spaceId: string | null; description: string | null; widthFt: number; heightFt: number; isDefault: boolean; active: boolean; sortOrder: number; hasBackground: boolean; fixedElements: RentalLayoutElement[] }
  shapes: RentalLayoutShape[]
  spaces: Array<{ id: string; name: string }>
}

export type RentalCatalogQuestionnaire = {
  owner: string
  canEdit: boolean
  template: RentalCatalogRow
  questions: RentalCatalogRow[]
}
